import React, { useState } from "react";
import Title from "../../components/common/title/Title";
import Button from "../../components/common/button/Button";
import { postApi, putApi } from "../../services/api/globalApi";
import { useLocation, useNavigate } from "react-router";
import {
  successMessage,
  errorMessage,
  warningMessage,
} from "../../utils/toastify-message/ToastifyMessage";
import { BENEFIT_ICONS, BenefitIcon } from "./benefitIcons";
import { PiXFill } from "react-icons/pi";

const { forwardRef, useRef, useImperativeHandle } = React;

const IconPickerModal = forwardRef(({ onSelect }, ref) => {
  const [showModal, setShowModal] = useState(false);

  useImperativeHandle(ref, () => ({
    showModal() {
      setShowModal(true);
    },
  }));

  const handleSelect = (key) => {
    onSelect(key);
    setShowModal(false);
  };

  return (
    <>
      <div
        className={`fixed top-0 right-0 w-full h-full bg-black/40 transition-all z-30 ${
          !showModal && "hidden"
        }`}
        onClick={() => setShowModal(false)}
      />
      <div
        className={`w-[340px] sm:w-[600px] py-4 max-h-[80vh] overflow-y-auto bg-white dark:bg-gray-900 shadow-md z-40 rounded-3xl p-6 transition-all fixed top-0 right-[50%] mr-[-170px] sm:mr-[-300px] opacity-0 invisible ${
          showModal && "!mt-20 !opacity-100 !visible"
        }`}
      >
        <div className="w-full flex items-center justify-between pb-4 border-b-2 mb-4">
          <h4 className="font-bold text-gray-800 dark:text-white">انتخاب آیکون</h4>
          <PiXFill
            onClick={() => setShowModal(false)}
            size={26}
            className="text-red-400 transition-all hover:text-red-600 cursor-pointer"
          />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {BENEFIT_ICONS.map((icon) => (
            <button
              key={icon.key}
              type="button"
              onClick={() => handleSelect(icon.key)}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 dark:border-gray-600 dark:hover:border-green-500 dark:hover:bg-green-900/30 text-gray-600 dark:text-gray-300 transition-all cursor-pointer"
            >
              <BenefitIcon iconKey={icon.key} className="w-6 h-6" />
              <span className="text-[0.6rem] leading-tight text-center">{icon.label}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
});

export default function AffiliateBenefitForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = !!location.state;

  const [title, setTitle] = useState(location.state?.title || "");
  const [desc, setDesc] = useState(location.state?.desc || "");
  const [iconKey, setIconKey] = useState(
    location.state?.iconKey || BENEFIT_ICONS[0].key
  );
  const [loading, setLoading] = useState(false);

  const modalRef = useRef();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return warningMessage("لطفا عنوان را وارد کنید");
    if (!desc.trim()) return warningMessage("لطفا توضیح را وارد کنید");

    setLoading(true);
    try {
      if (isEdit) {
        await putApi({
          path: `admin/affiliate-benefits/${location.state._id}`,
          body: JSON.stringify({ title, desc, iconKey }),
        });
        successMessage("مزیت با موفقیت ویرایش شد");
      } else {
        await postApi({
          path: "admin/affiliate-benefits",
          body: JSON.stringify({ title, desc, iconKey }),
        });
        successMessage("مزیت با موفقیت اضافه شد");
      }
      navigate("/affiliate-benefit-list");
    } catch (error) {
      console.error(error);
      errorMessage("خطایی رخ داد");
    } finally {
      setLoading(false);
    }
  };

  const selectedLabel = BENEFIT_ICONS.find((i) => i.key === iconKey)?.label;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6">
      <Title title={isEdit ? "ویرایش مزیت نمایندگی" : "افزودن مزیت نمایندگی"} />
      <form onSubmit={handleSubmit} className="mt-6 space-y-6">

        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
            عنوان مزیت
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="مثال: حاشیه سود بالا"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4 items-start">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              توضیح کوتاه
            </label>
            <textarea
              rows={3}
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="یک جمله‌ی کوتاه که مزیت را توضیح می‌دهد"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 resize-y"
            />
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              آیکون
            </label>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-50 dark:bg-green-900/30 flex items-center justify-center text-green-600">
                <BenefitIcon iconKey={iconKey} className="w-7 h-7" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{selectedLabel}</p>
                <button
                  type="button"
                  onClick={() => modalRef.current.showModal()}
                  className="text-sm text-green-600 hover:text-green-700 border border-green-500 hover:border-green-600 rounded-lg px-3 py-1.5 transition-all"
                >
                  تغییر آیکون
                </button>
              </div>
            </div>
          </div>
        </div>

        <Button
          text={loading ? "در حال ذخیره..." : isEdit ? "ویرایش" : "افزودن"}
          type="submit"
          color="success"
          disabled={loading}
        />
      </form>

      <IconPickerModal ref={modalRef} onSelect={setIconKey} />
    </div>
  );
}